import { Fragment, useLayoutEffect, useRef } from "react";
import { format, isSameDay, isToday, isYesterday } from "date-fns";
import { es } from "date-fns/locale";
import { ArrowUp, Loader2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WhatsAppChatMessage } from "@/services/whatsapp";
import { ChatMessageBubble } from "./ChatMessageBubble";

function formatDayLabel(date: Date): string {
  if (isToday(date)) return "Hoy";
  if (isYesterday(date)) return "Ayer";
  return format(date, "d 'de' MMMM yyyy", { locale: es });
}

interface ChatMessageListProps {
  chatId: string;
  messages: WhatsAppChatMessage[];
  isLoading: boolean;
  isLoadingEarlier: boolean;
  hasEarlier: boolean;
  onLoadEarlier: () => Promise<void>;
}

export function ChatMessageList({
  chatId,
  messages,
  isLoading,
  isLoadingEarlier,
  hasEarlier,
  onLoadEarlier,
}: ChatMessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Altura previa a cargar mensajes antiguos, para mantener la posición del scroll
  const prevScrollHeightRef = useRef<number | null>(null);
  const lastMessageIdRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    if (prevScrollHeightRef.current !== null) {
      el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = null;
      return;
    }

    // Solo se baja al final cuando llega un mensaje nuevo (no al actualizar un ack)
    const lastId = messages[messages.length - 1]?.id ?? null;
    if (lastId !== lastMessageIdRef.current) {
      el.scrollTop = el.scrollHeight;
      lastMessageIdRef.current = lastId;
    }
  }, [messages]);

  const handleLoadEarlier = async () => {
    if (scrollRef.current) prevScrollHeightRef.current = scrollRef.current.scrollHeight;
    await onLoadEarlier();
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="ml-2 text-sm text-muted-foreground">Cargando mensajes...</span>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <MessageCircle className="h-7 w-7 text-primary" />
        </div>
        <div>
          <p className="font-medium">Aún no hay mensajes</p>
          <p className="text-sm text-muted-foreground">Escribe el primer mensaje para iniciar la conversación.</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto bg-muted/30 px-4 py-3">
      <div className="flex flex-col gap-2">
        <div className="flex justify-center pb-1">
          {hasEarlier ? (
            <Button
              variant="outline"
              size="sm"
              className="h-7 rounded-full text-xs bg-background"
              onClick={handleLoadEarlier}
              disabled={isLoadingEarlier}
            >
              {isLoadingEarlier ? (
                <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
              ) : (
                <ArrowUp className="mr-1.5 h-3 w-3" />
              )}
              Cargar mensajes anteriores
            </Button>
          ) : (
            <span className="text-[11px] text-muted-foreground">Inicio de la conversación</span>
          )}
        </div>

        {messages.map((message, index) => {
          const date = new Date(message.timestamp * 1000);
          const prev = messages[index - 1];
          const showDay = !prev || !isSameDay(date, new Date(prev.timestamp * 1000));
          return (
            <Fragment key={message.id}>
              {showDay && (
                <div className="sticky top-0 z-10 flex justify-center py-1">
                  <span className="rounded-full border bg-background px-3 py-0.5 text-[11px] font-medium text-muted-foreground shadow-sm">
                    {formatDayLabel(date)}
                  </span>
                </div>
              )}
              <ChatMessageBubble message={message} chatId={chatId} />
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
